import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type PrescriptionRecord } from '../../api';
import PrescriptionQuotes from '../../components/PrescriptionQuotes';
import {
  deliveryEtaLabel, pharmacyPublicApi, storePlaceLine,
  type PublicStoreCategory, type PublicStoreSummary,
} from '../../pharmacyPublicApi';

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconPharmacy({ color }: { color: string }) {
  return (
    <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6l1 4H8L9 3z" />
      <rect x="4" y="7" width="16" height="14" rx="2" />
      <path d="M9 11h6" /><path d="M12 8v10" />
    </svg>
  );
}

function IconSearch({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
    </svg>
  );
}




function IconVerified({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconChevron({ color }: { color: string }) {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}


// ─── Filters ─────────────────────────────────────────────────────────────────

/** Each chip is a fact on the store's own record, not a word in a tag list. */
const FILTER_CHIPS = ['Delivery', 'Pickup', 'Verified', 'In stock'];

// ─── Store card ──────────────────────────────────────────────────────────────

/** A chip in the pharmacy's own terms — never a label this app decided on. */
function StoreChip({ label, tone = 'muted' }: { label: string; tone?: 'muted' | 'good' | 'warn' }) {
  const c = {
    muted: { fg: T.textSec, bg: `${T.textSec}12`, bd: `${T.textSec}25` },
    good: { fg: '#047857', bg: '#ECFDF5', bd: '#A7F3D0' },
    warn: { fg: '#B45309', bg: '#FFFBEB', bd: '#FDE68A' },
  }[tone];
  return (
    <span style={{
      fontFamily: T.font, fontSize: 10.5, fontWeight: 700, color: c.fg,
      background: c.bg, border: `1px solid ${c.bd}`, borderRadius: 20, padding: '3px 9px',
      display: 'inline-block', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

function PharmacyCard({ store, prescriptionId }: { store: PublicStoreSummary; prescriptionId?: string | null }) {
  const navigate = useNavigate();
  const place = storePlaceLine(store);
  const eta = deliveryEtaLabel(store.deliveryRules);
  const inStock = store.catalog?.inStock ?? 0;
  const qs = prescriptionId ? `?prescriptionId=${prescriptionId}` : '';

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 16, padding: 16, boxShadow: T.shadow }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 42, height: 42, borderRadius: 8, flexShrink: 0, background: '#0596691F', display: 'grid', placeItems: 'center' }}>
          <IconPharmacy color="#059669" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.text }}>{store.name}</span>
            {store.verified && <IconVerified color={T.teal} />}
          </div>
          {place && (
            <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.textTer, marginTop: 1 }}>{place}</div>
          )}
          {/* The store's own published hours — not a "24/7" tag someone typed
              on a directory row. */}
          {store.openingHours && (
            <div style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, marginTop: 2 }}>{store.openingHours}</div>
          )}
          {/* Only once somebody actually reviewed the store. */}
          {store.rating != null && (
            <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.textTer, marginTop: 2 }}>
              ★ {store.rating.toFixed(1)} · {store.reviewCount} review{store.reviewCount === 1 ? '' : 's'}
            </div>
          )}
        </div>
        <IconChevron color={T.textTer} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {store.pickup && <StoreChip label="Pickup" />}
        {store.delivery && <StoreChip label="Delivery" />}
        {/* The store's real catalogue depth — a card may only claim "in stock"
            when the store has stock on something it lists. */}
        <StoreChip
          label={inStock > 0 ? `${inStock} in stock` : 'Nothing in stock right now'}
          tone={inStock > 0 ? 'good' : 'warn'}
        />
        {eta && <StoreChip label={eta} />}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={() => navigate(`/patient/care-services/pharmacy/${store.slug}${qs}`)}
          style={{
            flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#059669',
            background: '#05966910', border: '1.5px solid #05966930', borderRadius: 10,
            padding: '9px 0', cursor: 'pointer',
          }}
        >
          View Store
        </button>
        <button
          onClick={() => navigate(`/patient/care-services/pharmacy/${store.slug}/order${qs}`)}
          style={{
            flex: 1, fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white,
            background: '#059669', border: 'none', borderRadius: 10, padding: '9px 0', cursor: 'pointer',
          }}
        >
          Order
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PharmacyDiscovery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const prescriptionId = searchParams.get('prescriptionId');
  // Real signed prescriptions from the backend (the local store never holds
  // prescriptions — doctors issue them via the doctor panel).
  const [prescriptions, setPrescriptions] = useState<PrescriptionRecord[]>([]);
  useEffect(() => {
    let alive = true;
    patientApi.getPrescriptions()
      .then((rows) => { if (alive) setPrescriptions(rows); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const linkedPrescription = prescriptionId ? prescriptions.find(rx => rx.id === prescriptionId) : null;
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  /**
   * The pharmacies that have actually opened an online store.
   *
   * This page used to list the marketplace directory instead — every pharmacy
   * on file, whether or not a patient could buy anything from it. On the live
   * network that was seven rows: two real stores, two leftover test records,
   * a duplicate listing, and two shops with no store at all, five of them
   * labelled "Online ordering not enabled". The directory row also carries
   * none of what a buyer needs — the delivery fee, the minimum order, the
   * promised ETA, what is in stock — so the card filled the gap from
   * free-text `tags`, which is how a shop whose published hours are
   * "Mon–Sat 9:00–21:00" came to be shown as "24/7 · Always open".
   */
  const [stores, setStores] = useState<PublicStoreSummary[]>([]);
  const [categories, setCategories] = useState<PublicStoreCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await pharmacyPublicApi.listStores();
      if (aliveRef.current) setStores(Array.isArray(rows) ? rows : []);
    } catch {
      if (aliveRef.current) { setStores([]); setError('Could not load pharmacies. Please try again.'); }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchStores(); }, [fetchStores]);

  // Supporting detail: a network where nobody categorised anything draws none.
  useEffect(() => {
    let alive = true;
    pharmacyPublicApi.listCategories()
      .then((rows) => { if (alive) setCategories(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (alive) setCategories([]); });
    return () => { alive = false; };
  }, []);

  const toggleFilter = (chip: string) => {
    setActiveFilters((prev) =>
      prev.includes(chip) ? prev.filter((f) => f !== chip) : [...prev, chip]
    );
  };

  // Searching and filtering happen here because the storefront list is the
  // whole network — and each test is the store's own record, not a tag.
  const filteredProviders = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return stores.filter((s) => {
      if (activeFilters.includes('Delivery') && !s.delivery) return false;
      if (activeFilters.includes('Pickup') && !s.pickup) return false;
      if (activeFilters.includes('Verified') && !s.verified) return false;
      if (activeFilters.includes('In stock') && (s.catalog?.inStock ?? 0) === 0) return false;
      if (!needle) return true;
      return `${s.name} ${storePlaceLine(s)} ${s.nh12 ?? ''}`.toLowerCase().includes(needle);
    });
  }, [stores, activeFilters, search]);

  return (
    <div
      style={{
        background: T.bg,
        fontFamily: T.font,
        margin: '0 auto',
        paddingBottom: 100,
      }}
    >
      {/* Header */}
      <div
        style={{
          background: `linear-gradient(135deg, #059669 0%, #047857 100%)`,
          padding: '20px 16px 56px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          }}
        />
        <h1
          style={{
            fontFamily: T.font,
            fontSize: 24,
            fontWeight: 800,
            color: T.white,
            margin: '0 0 6px',
          }}
        >
          Pharmacy
        </h1>
        <p
          style={{
            fontFamily: T.font,
            fontSize: 13,
            color: 'rgba(255,255,255,0.75)',
            margin: 0,
          }}
        >
          Order medicines, upload prescriptions, home delivery
        </p>
      </div>

      {/* Search bar */}
      <div style={{ padding: '0 16px', marginTop: -28, position: 'relative', zIndex: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: T.white,
            borderRadius: 14,
            padding: '11px 14px',
            boxShadow: T.shadowLg ?? '0 4px 20px rgba(0,0,0,0.12)',
            border: `1px solid ${T.border}`,
          }}
        >
          <IconSearch color={T.textTer} />
          <input
            type="text"
            placeholder="Search pharmacy, medicine..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontFamily: T.font,
              fontSize: 13,
              color: T.text,
              background: 'transparent',
              padding: 0,
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: T.textTer,
                fontSize: 16,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Prescription context banner */}
      {linkedPrescription && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 14, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14 }}>📋</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#059669', fontFamily: T.font }}>Order from Prescription</span>
            </div>
            <p style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, margin: '0 0 2px' }}>
              {linkedPrescription.doctorName} · {linkedPrescription.medicines.length} medicine{linkedPrescription.medicines.length !== 1 ? 's' : ''}
            </p>
            <p style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, margin: 0 }}>
              Select a pharmacy below to order your prescribed medicines
            </p>
          </div>
        </div>
      )}

      {/* Filter chips */}
      <div style={{ padding: '14px 16px 0', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 8, width: 'max-content' }}>
          {FILTER_CHIPS.map((chip) => {
            const active = activeFilters.includes(chip);
            return (
              <button
                key={chip}
                onClick={() => toggleFilter(chip)}
                style={{
                  fontFamily: T.font,
                  fontSize: 12,
                  fontWeight: 600,
                  color: active ? T.white : T.textSec,
                  background: active ? '#059669' : T.white,
                  border: `1px solid ${active ? '#059669' : T.border}`,
                  borderRadius: 20,
                  padding: '7px 14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  boxShadow: T.shadow,
                  transition: 'background 0.15s, color 0.15s',
                }}
              >
                {chip}
              </button>
            );
          })}
        </div>
      </div>

      {/* What these stores actually stock, as they filed it. Supporting
          detail only — it is not a filter, because a category counts listings
          across stores and this list is of stores. */}
      {categories.length > 0 && (
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.textTer, marginBottom: 6 }}>Across these stores</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {categories.map((c) => <StoreChip key={`${c.kind}-${c.tag}`} label={`${c.tag} · ${c.count}`} />)}
          </div>
        </div>
      )}

      {/* Results */}
      <div style={{ padding: '16px 16px 0', paddingBottom: 8 }}>
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: `3px solid ${T.border}`,
                borderTopColor: '#059669',
                margin: '0 auto 12px',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, margin: 0 }}>
              Loading pharmacies...
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div
            style={{
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: 12,
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <p style={{ fontFamily: T.font, fontSize: 13, color: '#B91C1C', margin: '0 0 10px' }}>
              {error}
            </p>
            <button
              onClick={() => void fetchStores()}
              style={{
                fontFamily: T.font,
                fontSize: 12,
                fontWeight: 700,
                color: T.white,
                background: '#059669',
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#05966912',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
              }}
            >
              <IconPharmacy color="#059669" />
            </div>
            <p
              style={{
                fontFamily: T.font,
                fontSize: 14,
                fontWeight: 700,
                color: T.text,
                margin: '0 0 6px',
              }}
            >
              No pharmacy store found
            </p>
            <p style={{ fontFamily: T.font, fontSize: 12, color: T.textSec, margin: 0 }}>
              {search
                ? `No store matches "${search}".`
                : 'No pharmacy has opened an online store yet.'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* The comparison this banner has always promised. Choosing a
                prescription prices it against every public catalogue. */}
            {prescriptions.length > 0 && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #0D9488 0%, #0F766E 100%)',
                  borderRadius: 14,
                  padding: '14px 16px',
                  boxShadow: '0 2px 8px rgba(13,148,136,0.25)',
                }}
              >
                <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.white, marginBottom: 4 }}>
                  You have {prescriptions.length} prescription{prescriptions.length !== 1 ? 's' : ''} on record.
                </div>
                <div style={{ fontFamily: T.font, fontSize: 11, color: 'rgba(255,255,255,0.75)', marginBottom: 10 }}>
                  Pick one to see what each pharmacy charges for it.
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {prescriptions.slice(0, 5).map((rx) => {
                    const on = prescriptionId === rx.id;
                    return (
                      <button
                        key={rx.id}
                        onClick={() => setSearchParams(on ? {} : { prescriptionId: rx.id }, { replace: true })}
                        style={{
                          fontFamily: T.font, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          color: on ? '#0F766E' : T.white,
                          background: on ? T.white : 'rgba(255,255,255,0.15)',
                          border: '1px solid rgba(255,255,255,0.45)',
                          borderRadius: 18, padding: '6px 12px',
                        }}
                      >
                        {on ? 'Showing · ' : ''}{rx.doctorName || 'Prescription'}
                        {rx.appointmentDate ? ` · ${new Date(rx.appointmentDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {prescriptionId && <PrescriptionQuotes prescriptionId={prescriptionId} />}
            <p
              style={{
                fontFamily: T.font,
                fontSize: 12,
                color: T.textSec,
                margin: '0 0 4px',
              }}
            >
              {filteredProviders.length === 1 ? '1 store' : `${filteredProviders.length} stores`}
            </p>
            {filteredProviders.map((s) => (
              <PharmacyCard key={s.slug} store={s} prescriptionId={prescriptionId} />
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          Each pharmacy lists, prices and stocks its own medicines, so an order goes to one store. Prices and availability are that pharmacy's own.
        </span>
      </div>
    </div>
  );
}
